import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingCart, Check } from "lucide-react";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/hooks/use-locale";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackCTAClick } from "@/lib/analytics";
import JsonLd from "@/components/JsonLd";
import FaqGroupsSection, { toFaqGroup } from "@/components/FaqGroupsSection";
import { itemListSchema } from "@/lib/seo-schemas";
import { characters, getCharacterField } from "@/lib/characters";

const AMAZON_STORE = "https://www.amazon.com/stores/Kalianii-MotherlyLoveandLight/page/2E97BFF2-3CBA-47CB-8B3D-3D77AF27DFD0";

const AnimatedSection = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  const [ref, isVisible] = useScrollAnimation<HTMLElement>();
  return (
    <section
      ref={ref}
      className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
    >
      {children}
    </section>
  );
};

const Index = () => {
  const { t } = useTranslation();
  const { prefix, currentLang } = useLocale();

  const charactersList = itemListSchema({
    name: "Queebi Land Characters",
    url: "https://queebi.com/queebi-land-characters",
    items: characters.map((c) => ({
      name: getCharacterField(c, "fullName", currentLang),
      url: `https://queebi.com/${c.slug}`,
      image: c.image,
      description: getCharacterField(c, "bio", currentLang),
    })),
  });

  const selGroup = toFaqGroup("sel", t("faq.groups.sel", { returnObjects: true }));
  const homeGroups = [selGroup].filter(Boolean) as NonNullable<ReturnType<typeof toFaqGroup>>[];

  const whyPaci = [
    { title: t("index.whyPaci1Title"), desc: t("index.whyPaci1Desc") },
    { title: t("index.whyPaci2Title"), desc: t("index.whyPaci2Desc") },
    { title: t("index.whyPaci3Title"), desc: t("index.whyPaci3Desc") },
    { title: t("index.whyPaci4Title"), desc: t("index.whyPaci4Desc") },
  ];

  return (
    <>
      <PageHead
        title={t("index.seoTitle")}
        description={t("index.seoDesc")}
        canonical="https://queebi.com/"
        image="/images/og/home-1200x630.jpg"
        imageAlt="Queebi PaciBuddy — plush pacifier holder with stuffed animal"
        keywords="Queebi, PaciBuddy, plush pacifier holder, pacifier with stuffed animal, paci plushies, paci plush, stuffed animal pacifier holder, wubbanub alternative, nookums alternative, baby shower gift, newborn baby gift, gender neutral baby gift, Queebi Land, social emotional learning, SEL baby products"
      />
      <JsonLd data={charactersList} />

      {/* Hero — editorial, no frame */}
      <section className="bg-background">
        <div className="container pt-10 md:pt-20 pb-12 md:pb-24 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="order-2 md:order-1 md:col-span-6 md:pr-4">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-foreground/40" />
              <p className="text-[11px] uppercase tracking-[0.32em] text-foreground/60">
                Queebi · PaciBuddy
              </p>
            </div>
            <h1 className="font-heading font-light text-[2.5rem] leading-[1.05] md:text-6xl md:leading-[1.02] tracking-tight text-foreground mb-8">
              {t("index.heroTitle1")}
              <br />
              <em className="not-italic font-normal text-accent">{t("index.heroTitle2")}</em>
            </h1>
            <p className="text-[15px] md:text-base text-foreground/70 mb-10 leading-[1.8] max-w-xl">
              {t("index.heroSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Button asChild size="lg" className="rounded-none bg-foreground text-background font-medium tracking-wide px-8 h-12 hover:bg-foreground/85 transition-colors">
                <Link to={`${prefix}/queebi-products/pacibuddy`} onClick={() => trackCTAClick('hero_shop_pacibuddy')}>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {t("index.meetCharacters")}
                </Link>
              </Button>
              <Link
                to={`${prefix}/queebi-land-characters`}
                onClick={() => trackCTAClick('hero_explore_queebi_land')}
                className="group inline-flex items-center gap-2 text-sm tracking-wide text-foreground/80 hover:text-foreground"
              >
                <span className="border-b border-foreground/40 group-hover:border-foreground pb-0.5">{t("index.shopProducts")}</span>
                <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
              </Link>
            </div>
          </div>
          <div className="order-1 md:order-2 md:col-span-6 relative">
            <img
              src="/images/lifestyle/baby-crib-crab.jpg"
              alt="Baby in crib holding Queebi PaciBuddy crab plush pacifier"
              className="w-full h-[420px] md:h-[640px] object-cover"
              width={900}
              height={1100}
              loading="eager"
              decoding="async"
              // @ts-ignore
              fetchpriority="high"
            />
          </div>
        </div>
      </section>

      {/* Shop the Collection — editorial product row */}
      <AnimatedSection className="bg-background pt-4 pb-20 md:pb-28">
        <div className="container">
          <div className="flex items-end justify-between mb-10 md:mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-foreground/40" />
                <p className="text-[11px] uppercase tracking-[0.32em] text-foreground/60">The Collection</p>
              </div>
              <h2 className="font-heading font-light text-3xl md:text-4xl tracking-tight">Shop PaciBuddy</h2>
            </div>
            <Link
              to={`${prefix}/queebi-products`}
              onClick={() => trackCTAClick('shop_collection_view_all')}
              className="hidden md:inline-flex items-center gap-2 text-sm tracking-wide text-foreground/80 hover:text-foreground"
            >
              <span className="border-b border-foreground/40 group-hover:border-foreground pb-0.5">View all</span>
              <span aria-hidden>→</span>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {[
              { img: "/images/products/pacibuddy-crab-product.jpg", name: "Crab PaciBuddy", note: "Calm & cozy" },
              { img: "/images/lifestyle/pacibuddy-deer.jpg", name: "Deer PaciBuddy", note: "Gentle & curious" },
              { img: "/images/lifestyle/collection-bowl-hero.jpg", name: "The Six Friends", note: "Complete set" },
              { img: "/images/lifestyle/baby-deer.jpg", name: "In Use", note: "From day one" },
            ].map((p) => (
              <Link
                key={p.name}
                to={`${prefix}/queebi-products`}
                onClick={() => trackCTAClick(`shop_collection_${p.name}`)}
                className="group block"
              >
                <div className="overflow-hidden bg-card">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    width={600}
                    height={600}
                    loading="lazy"
                  />
                </div>
                <div className="mt-4 flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-foreground/50 mb-1">{p.note}</p>
                    <h3 className="text-sm md:text-base font-medium text-foreground">{p.name}</h3>
                  </div>
                  <span aria-hidden className="text-foreground/40 group-hover:text-foreground group-hover:translate-x-1 transition">→</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Why PaciBuddy */}
      <AnimatedSection className="bg-card py-16 md:py-24">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-heading mb-3">{t("index.whyPaciTitle")}</h2>
            <p className="text-muted-foreground">{t("index.whyPaciSub")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {whyPaci.map((item, i) => (
              <div key={i} className="bg-background rounded-2xl p-6 border border-border/50 hover:border-accent/50 transition-colors">
                <div className="h-9 w-9 rounded-full bg-accent/15 text-accent inline-flex items-center justify-center mb-3">
                  <Check className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-base mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
            <Button asChild className="rounded-full bg-foreground text-background font-bold hover:bg-foreground/90 transition-transform duration-200 hover:scale-105">
              <a href={AMAZON_STORE} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('whypaci_shop_amazon')}>
                <ShoppingCart className="h-4 w-4 mr-2" />
                {t("index.shopOnAmazonCta")}
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full font-bold border-foreground transition-transform duration-200 hover:scale-105">
              <Link to={`${prefix}/queebi-products`} onClick={() => trackCTAClick('whypaci_view_all')}>{t("index.viewAllProducts")}</Link>
            </Button>
          </div>
        </div>
      </AnimatedSection>

      {/* Play Feel Grow */}
      <AnimatedSection className="bg-background py-20 md:py-24">
        <div className="container">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-center mb-16">
            {t("index.play")}. <span className="text-accent">{t("index.feel")}.</span> {t("index.grow")}.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-5xl mx-auto text-center">
            <div className="transition-transform duration-300 hover:-translate-y-2">
              <img src="/images/icons/play.png" alt={`Icon representing play — ${t("index.play")}`} className="h-24 w-24 mx-auto mb-6 object-contain" width={96} height={96} loading="lazy" />
              <h3 className="font-bold text-xl mb-3">{t("index.play")}</h3>
              <p className="text-muted-foreground text-base">{t("index.playDesc")}</p>
            </div>
            <div className="transition-transform duration-300 hover:-translate-y-2">
              <img src="/images/icons/feel.png" alt={`Icon representing feelings — ${t("index.feel")}`} className="h-24 w-24 mx-auto mb-6 object-contain" width={96} height={96} loading="lazy" />
              <h3 className="font-bold text-xl mb-3">{t("index.feel")}</h3>
              <p className="text-muted-foreground text-base">{t("index.feelDesc")}</p>
            </div>
            <div className="transition-transform duration-300 hover:-translate-y-2">
              <img src="/images/icons/grow.png" alt={`Icon representing growth — ${t("index.grow")}`} className="h-24 w-24 mx-auto mb-6 object-contain" width={96} height={96} loading="lazy" />
              <h3 className="font-bold text-xl mb-3">{t("index.grow")}</h3>
              <p className="text-muted-foreground text-base">{t("index.growDesc")}</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <Button asChild className="rounded-full bg-primary text-primary-foreground font-bold transition-transform duration-200 hover:scale-105">
              <Link to={`${prefix}/queebi-land-characters`} onClick={() => trackCTAClick('meet_friends')}>{t("index.meetFriends")}</Link>
            </Button>
          </div>
        </div>
      </AnimatedSection>

      {/* Tools / SEL narrative */}
      <AnimatedSection className="bg-card py-20 md:py-24">
        <div className="container max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-8">{t("index.simpleToolsTitle")}</h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-4">{t("index.simpleToolsP1")}</p>
          <p className="text-muted-foreground text-lg leading-relaxed mb-8">{t("index.simpleToolsP2")}</p>
          <Button asChild variant="outline" className="rounded-full font-bold border-foreground transition-transform duration-200 hover:scale-105">
            <Link to={`${prefix}/activities`} onClick={() => trackCTAClick('play_with_queebi')}>{t("index.playWithQueebi")}</Link>
          </Button>
        </div>
      </AnimatedSection>

      {/* IP in Action - Queebi Land teaser */}
      <AnimatedSection className="py-20 md:py-24 bg-queebi-teal/30">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-heading mb-6">{t("index.ipTitle")}</h2>
            <p className="text-foreground/80 text-lg leading-relaxed">{t("index.ipDesc")}</p>
          </div>
          <div className="grid grid-cols-12 gap-x-6 md:gap-x-10 gap-y-8 md:gap-y-12">
            <figure className="col-span-12 md:col-span-5">
              <img src="/images/lifestyle/baby-crab-sleep.jpg" alt="Baby sleeping with Queebi PaciBuddy crab and storybook" className="w-full h-80 md:h-[420px] object-cover" width={600} height={420} loading="lazy" />
              <figcaption className="mt-3 text-[10px] uppercase tracking-[0.28em] text-foreground/55">01 · Quiet moments</figcaption>
            </figure>
            <figure className="col-span-12 md:col-span-6 md:col-start-7 md:mt-20">
              <img src="/images/lifestyle/collection-bowl-hero.jpg" alt="Queebi PaciBuddy collection — deer, fox, koala, lion, crab, crocodile" className="w-full h-72 md:h-[360px] object-cover" width={700} height={360} loading="lazy" />
              <figcaption className="mt-3 text-[10px] uppercase tracking-[0.28em] text-foreground/55">02 · Six friends, six feelings</figcaption>
            </figure>
            <figure className="col-span-12 md:col-span-7 md:col-start-3 md:-mt-6">
              <img src="/images/lifestyle/baby-deer.jpg" alt="Baby holding Queebi PaciBuddy deer plush in crib" className="w-full h-80 md:h-[460px] object-cover" width={800} height={460} loading="lazy" />
              <figcaption className="mt-3 text-[10px] uppercase tracking-[0.28em] text-foreground/55">03 · Loved from day one</figcaption>
            </figure>
          </div>
          <div className="text-center mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild variant="outline" className="rounded-full font-bold border-foreground transition-transform duration-200 hover:scale-105">
              <Link to={`${prefix}/queebi-land-characters`} aria-label="Explore Queebi Land characters" onClick={() => trackCTAClick('ip_explore_land')}>{t("index.learnMore")}</Link>
            </Button>
            <Button asChild className="rounded-full bg-foreground text-background font-bold hover:bg-foreground/90 transition-transform duration-200 hover:scale-105">
              <a href={AMAZON_STORE} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('ip_shop_amazon')}>{t("nav.shopOnAmazon")}</a>
            </Button>
          </div>
        </div>
      </AnimatedSection>

      {/* FAQ */}
      <FaqGroupsSection title={t("faq.title")} groups={homeGroups} />

      {/* Marquee */}
      <section className="bg-foreground py-2 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {Array.from({ length: 10 }).map((_, i) => (
            <span key={i} className="text-base font-bold text-background mx-8">
              {t("index.marquee")}
            </span>
          ))}
        </div>
      </section>
    </>
  );
};

export default Index;
